/**
 * System variables store — 2D-style named environment variables.
 * Persisted; read by behavior integrations and written by VAR.<name>
 * commands (sysvarCommands). Keys mirror 2D system variable names.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface SystemVars {
    /** 1 = dialog boxes (default here), 0 = command-line file prompts. */
    FILEDIA: number;
    /** 1 = mirrored text flips (default), 0 = text stays readable. */
    MIRRTEXT: number;
    /** 1 = select-before-command allowed (default), 0 = command-first only. */
    PICKFIRST: number;
    /** Multi-core regen hint (informational; renderer is demand-based). */
    WHIPTHREAD: number;
    /** Dynamic preview during drag operations: 1 auto / 0 off. */
    DRAGMODE: number;
    /** Selection highlight display: 1 on / 0 off. */
    HIGHLIGHT: number;
    /** Incremental save ratio (0 = always full write). */
    ISAVEPERCENT: number;
    /** Auto-save interval in minutes. */
    SAVETIME: number;
    /** 1 = dialog UI for some commands, 0 = command-line. */
    CMDDIA: number;
    /** Trim/extend treat implied edge extensions as boundaries: 1/0. */
    EDGEMODE: number;
    /** Text editor: 0 on-screen, 1 dialog, 2 enhanced on-screen. */
    TEXTED: number;
    /** Current annotation scale denominator (1:100 → 100). */
    CANNOSCALE: number;
    /** Fill display for hatches/solids: 1 filled, 0 outlines. */
    FILLMODE: number;
    /** Hatch boundary generation: 0 polyline, 1 region. */
    HPBOUND: number;
    /** Draw order display support level 0-3. */
    DRAWORDERCTL: number;
    /** Middle mouse button: 1 = pan, 0 = shortcut menu. */
    MBUTTONPAN: number;
    /** Crosshair size percent 1-100. */
    CURSORSIZE: number;
    /** Dynamic UCS auto-detect on hover over solid faces: 1/0. */
    UCSDETECT: number;
    /** 2D-style polar tracking on: 1/0. */
    POLARMODE: number;
    /** Polar tracking angle step (degrees). */
    POLARANG: number;
    /** POINT display mode (classic ACAD bit field: 0 dot, 1 cross, 2 plus,
     *  3 empty box, 4 filled box; +8 empty circle, +16 filled circle). */
    PDMODE: number;
    /** POINT display size: >0 absolute units, <0 percent of viewport. */
    PDSIZE: number;
    /** RECTANG fillet radius applied when the rectangle is created. */
    FILLETRADIUS: number;
    /** POLYGON edge count (default 5). */
    POLYGONSIDES: number;
    /** POLYGON mode: 0 = inscribed in circle, 1 = circumscribed about circle. */
    POLYGONTYPE: number;
    /** LISP rtos default unit mode (LUNITS: 1 sci, 2 decimal, 5 fractional). */
    LUNITS: number;
    /** LISP rtos default precision (LUPREC digits). */
    LUPREC: number;
    /** LISP angtos default mode (AUNITS: 0 degrees, 3 radians). */
    AUNITS: number;
    /** LISP angtos default precision (AUPREC digits). */
    AUPREC: number;
}

export const SYSTEM_VAR_DEFAULTS: SystemVars = {
    FILEDIA: 1,
    MIRRTEXT: 1,
    PICKFIRST: 1,
    WHIPTHREAD: 3,
    DRAGMODE: 1,
    HIGHLIGHT: 1,
    ISAVEPERCENT: 0,
    SAVETIME: 10,
    CMDDIA: 1,
    EDGEMODE: 1,
    TEXTED: 0,
    CANNOSCALE: 100,
    FILLMODE: 1,
    HPBOUND: 0,
    DRAWORDERCTL: 3,
    MBUTTONPAN: 1,
    CURSORSIZE: 100,
    UCSDETECT: 1,
    POLARMODE: 1,
    POLARANG: 90,
    PDMODE: 0,
    PDSIZE: 0,
    FILLETRADIUS: 0,
    POLYGONSIDES: 5,
    POLYGONTYPE: 0,
    LUNITS: 2,
    LUPREC: 4,
    AUNITS: 0,
    AUPREC: 4,
};

interface SystemVarState extends SystemVars {
    set: (key: keyof SystemVars, value: number) => void;
    getVar: (key: string) => number | undefined;
    resetAll: () => void;
}

export const useSystemVarsStore = create<SystemVarState>()(
    persist(
        (set, get) => ({
            ...SYSTEM_VAR_DEFAULTS,
            set: (key, value) => set({ [key]: value } as Partial<SystemVarState>),
            getVar: (key) => (get() as unknown as Record<string, unknown>)[key] as number | undefined,
            resetAll: () => set({ ...SYSTEM_VAR_DEFAULTS }),
        }),
        { name: 'yap-system-vars' }
    )
);
